import { Screen, ScreenHeader, Section } from "../components/AppShell";
import { Icon } from "../components/Icon";
import { Button, IconButton, Skeleton, Spinner, StateMessage } from "../components/ui";
import { LanguageToggle } from "../components/LanguageToggle";
import { usePrices, useRefreshPrices } from "../api/queries";
import { messagesOf } from "../api/errors";
import { openExternal } from "../telegram/webapp";
import { formatNumber, formatTime, formatToman } from "../i18n/format";
import { useT, type Strings } from "../i18n/strings";
import type { PriceItem, PriceKey } from "../api/types";
import shell from "../features/pieces/pieces.module.css";
import s from "../features/prices/prices.module.css";

const GROUPS: Array<{ key: keyof Strings["prices"]["groups"]; items: PriceKey[] }> = [
  { key: "gold", items: ["gold_18", "gold_24", "gold_mesghal"] },
  { key: "coins", items: ["coin_emami", "coin_bahar", "coin_half", "coin_quarter", "coin_gram"] },
  { key: "silver", items: ["silver_999", "silver_925"] },
  { key: "global", items: ["gold_ounce", "silver_ounce"] },
  { key: "currencies", items: ["usd", "eur", "aed"] },
];

function basisLabel(item: PriceItem, t: Strings): string {
  const { prices } = t;
  return {
    gram: prices.perGram,
    mesghal: prices.perMesghal,
    coin: prices.perCoin,
    ounce: prices.perOunce,
    unit: prices.perUnit,
  }[item.basis];
}

function PriceRow({ id, item }: { id: PriceKey; item: PriceItem }) {
  const t = useT();
  const rial = item.unit === "IRR";
  const change = item.change_pct;
  const sign = item.direction === "high" ? "+" : item.direction === "low" ? "−" : "";
  return (
    <div className={s.row}>
      <span className={s.rowLabel}>{t.prices.items[id]}</span>
      <span className={s.amount}>
        <span className={s.figure}>
          {rial ? formatToman(item.price) : formatNumber(item.price)}
        </span>
        <span className={s.amountUnit}>{rial ? t.prices.toman : t.prices.dollar}</span>
      </span>
      <span className={s.rowBasis}>{basisLabel(item, t)}</span>
      {change !== null && change !== 0 && (
        <span
          className={[
            s.change,
            item.direction === "high" && s.up,
            item.direction === "low" && s.down,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {sign}
          {formatNumber(Math.abs(change))}%
        </span>
      )}
    </div>
  );
}

export default function Prices() {
  const t = useT();
  const prices = usePrices();
  const refresh = useRefreshPrices();
  const board = prices.data;

  return (
    <Screen>
      <ScreenHeader
        sticky
        actions={
          <>
            {prices.isFetching || refresh.isPending ? (
              <span className={s.refreshSlot}>
                <Spinner />
              </span>
            ) : (
              <IconButton
                name="refresh"
                label={t.actions.refresh}
                onClick={() => refresh.mutate()}
              />
            )}
            <LanguageToggle />
          </>
        }
      >
        <div className={shell.masthead}>
          <h1 className={shell.mastheadTitle}>{t.prices.title}</h1>
          {board && (
            <p className={shell.mastheadCount}>
              {t.prices.asOf(formatTime(board.source_updated_at ?? board.fetched_at))}
            </p>
          )}
        </div>
      </ScreenHeader>

      {prices.isPending ? (
        <div className={s.board} aria-busy="true">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} height={44} radius="var(--radius-card)" />
          ))}
        </div>
      ) : !board ? (
        <StateMessage
          icon="alert"
          title={t.prices.unavailable}
          body={messagesOf(prices.error)[0]}
          action={
            <Button variant="secondary" onClick={() => prices.refetch()}>
              {t.actions.retry}
            </Button>
          }
        />
      ) : (
        <div className={s.board}>
          {board.stale && (
            <p className={s.notice}>
              <Icon name="alert" size={16} />
              {t.prices.stale}
            </p>
          )}

          {GROUPS.map((group) => {
            const rows = group.items.flatMap((id) => {
              const item = board.items[id];
              return item ? [{ id, item }] : [];
            });
            if (!rows.length) return null;
            return (
              <Section key={group.key} title={t.prices.groups[group.key]}>
                <div className={s.rows}>
                  {rows.map(({ id, item }) => (
                    <PriceRow key={id} id={id} item={item} />
                  ))}
                </div>
              </Section>
            );
          })}

          <div className={s.colophon}>
            <span>{t.prices.disclaimer}</span>
            <button
              type="button"
              className={s.sourceLink}
              onClick={() => openExternal(board.source_url)}
            >
              {t.prices.source}
            </button>
          </div>
        </div>
      )}
    </Screen>
  );
}
